import React from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink, useNavigate } from "react-router-dom";

const TEAL = "#06b6a4";

const RISK_LEVELS = [
  { label: "Conservative", color: "#15803d" },
  { label: "Moderately Conservative", color: "#22c55e" },
  { label: "Moderate", color: "#eab308" },
  { label: "Moderately Aggressive", color: "#f97316" },
  { label: "Aggressive", color: "#dc2626" },
];

// Current profile: Conservative (index 0). Needle points to start of arc (left).
const CURRENT_INDEX = 0;
const NEEDLE_ANGLE_DEG = 0; // 0 = left (Conservative) in our polarToCartesian

function RiskGauge() {
  const width = 320;
  const height = 180;
  const cx = width / 2;
  const cy = height - 10;
  const radius = 130;
  const segmentAngle = 180 / RISK_LEVELS.length; // 36 deg each

  const describeArc = (startAngle, endAngle) => {
    const start = polarToCartesian(cx, cy, radius, startAngle);
    const end = polarToCartesian(cx, cy, radius, endAngle);
    const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
    return ["M", start.x, start.y, "A", radius, radius, 0, largeArc, 1, end.x, end.y].join(" ");
  };

  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const angleRad = ((angleDeg - 180) * Math.PI) / 180;
    return {
      x: cx + r * Math.cos(angleRad),
      y: cy + r * Math.sin(angleRad),
    };
  };

  return (
    <div className="position-relative" style={{ width, height, margin: "0 auto" }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <linearGradient id="gaugeShadow" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#e5e7eb" />
            <stop offset="100%" stopColor="#f9fafb" />
          </linearGradient>
        </defs>
        {/* Arc segments: 0° (left = Conservative) to 180° (right = Aggressive) */}
        {RISK_LEVELS.map((seg, i) => {
          const startAngle = i * segmentAngle;
          const endAngle = (i + 1) * segmentAngle;
          const d = describeArc(startAngle, endAngle);
          return (
            <path
              key={seg.label}
              d={d}
              fill="none"
              stroke={seg.color}
              strokeWidth={24}
              strokeLinecap="round"
            />
          );
        })}
        {/* Needle pivot (orange circle) */}
        <circle cx={cx} cy={cy} r={12} fill="#ea580c" />
        <circle cx={cx} cy={cy} r={10} fill="#c2410c" />
        {/* Needle: use same coords as arc (0 = left = Conservative) */}
        <line
          x1={cx}
          y1={cy}
          x2={polarToCartesian(cx, cy, radius, NEEDLE_ANGLE_DEG).x}
          y2={polarToCartesian(cx, cy, radius, NEEDLE_ANGLE_DEG).y}
          stroke="#374151"
          strokeWidth={4}
          strokeLinecap="round"
        />
      </svg>
      {/* Labels below arc */}
      <div
        className="d-flex justify-content-between position-absolute"
        style={{
          left: 4,
          right: 4,
          bottom: 4,
          fontSize: "10px",
          fontWeight: "600",
        }}
      >
        {RISK_LEVELS.map((seg) => (
          <span key={seg.label} style={{ color: seg.color, flex: 1, textAlign: "center", lineHeight: 1.2 }}>
            {seg.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function RiskProfiling() {
  const navigate = useNavigate();
  const currentProfile = RISK_LEVELS[CURRENT_INDEX];
  const validUpto = "12-02-2027";

  const handleRenew = () => {
    navigate("/risk-profiling/questionnaire");
  };

  return (
    <>
      <ClientNavbar />
      <div className="page-content" style={{ background: "#f1f5f9", minHeight: "100vh" }}>
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
          <div className="card-body p-4">
            <h3 className="text-primary mb-2">Risk Profiling</h3>
            <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "none" }}>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <NavLink to="/overview">Dashboard</NavLink> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">Risk Profiling</li>
              </ol>
            </nav>
          </div>
        </div>

        <div
          className="card shadow-sm border-0 mx-auto"
          style={{
            maxWidth: 560,
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            overflow: "visible",
          }}
        >
          <div className="card-body p-5 text-center">
            <h4 className="mb-4" style={{ fontWeight: "700", color: "#0f172a" }}>
              Your Risk Profile is {currentProfile.label}
            </h4>

            <div className="mb-4" style={{ minHeight: 220 }}>
              <RiskGauge />
            </div>

            <p className="mb-4 text-muted" style={{ fontSize: "15px" }}>
              Valid upto {validUpto}
            </p>

            <button
              type="button"
              className="btn"
              style={{
                background: TEAL,
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "15px",
                padding: "10px 24px",
              }}
              onClick={handleRenew}
            >
              Renew Risk Profile
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
